import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import axios from 'axios';
import LeafletMap from './LeafletMap';
import { AlertTriangle, RefreshCw, User, Calendar, Droplet, MapPin, Mountain, SignpostBig, Trash, Bird, Image as ImageIcon, Ship, CheckCircle, XCircle, HandMetal, Star, Car, Coffee, Navigation, Phone, Waves, Search, Shield, Globe, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import TranslatedText from './TranslatedText';
import EmmeGuide, { useEmmeGuide, EmmeHelpButton } from './EmmeGuide';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Icona Tartaruga Marina (Caretta caretta) SVG
const SeaTurtleIcon = ({ className = "w-5 h-5", color = "white" }) => (
  <svg viewBox="0 0 24 24" fill={color} className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C10.5 2 9.2 2.7 8.3 3.8L6 3C5.4 2.8 4.8 3.2 4.8 3.8L5 6.3C3.9 7.3 3.2 8.7 3.2 10.3C3.2 10.8 3.3 11.3 3.4 11.7L1.5 13C1 13.3 0.9 14 1.3 14.4L3.2 16.3C3.6 17.5 4.4 18.5 5.4 19.2L5 21.5C4.9 22.1 5.4 22.6 6 22.5L8.5 21.8C9.5 22.3 10.7 22.5 12 22.5C13.3 22.5 14.5 22.3 15.5 21.8L18 22.5C18.6 22.6 19.1 22.1 19 21.5L18.6 19.2C19.6 18.5 20.4 17.5 20.8 16.3L22.7 14.4C23.1 14 23 13.3 22.5 13L20.6 11.7C20.7 11.3 20.8 10.8 20.8 10.3C20.8 8.7 20.1 7.3 19 6.3L19.2 3.8C19.2 3.2 18.6 2.8 18 3L15.7 3.8C14.8 2.7 13.5 2 12 2ZM12 4C13.1 4 14 4.9 14 6C14 7.1 13.1 8 12 8C10.9 8 10 7.1 10 6C10 4.9 10.9 4 12 4ZM8 10C8.8 10 9.5 10.7 9.5 11.5C9.5 12.3 8.8 13 8 13C7.2 13 6.5 12.3 6.5 11.5C6.5 10.7 7.2 10 8 10ZM16 10C16.8 10 17.5 10.7 17.5 11.5C17.5 12.3 16.8 13 16 13C15.2 13 14.5 12.3 14.5 11.5C14.5 10.7 15.2 10 16 10ZM12 12C13.7 12 15 13.3 15 15C15 16.7 13.7 18 12 18C10.3 18 9 16.7 9 15C9 13.3 10.3 12 12 12ZM7 16C7.6 16 8 16.4 8 17C8 17.6 7.6 18 7 18C6.4 18 6 17.6 6 17C6 16.4 6.4 16 7 16ZM17 16C17.6 16 18 16.4 18 17C18 17.6 17.6 18 17 18C16.4 18 16 17.6 16 17C16 16.4 16.4 16 17 16Z"/>
  </svg>
);

const CATEGORY_LABELS = {
  generic: 'Generico', bottiglie: 'Bottiglie', mozziconi: 'Mozziconi',
  tappi: 'Tappi', vetro: 'Vetro', alluminio: 'Alluminio',
  reti_pesca: 'Reti Pesca', reti_cozze: 'Reti Cozze', legno_verniciato: 'Legno'
};

const DIFFICULTY_LABELS = { facile: 'Facile', medio: 'Medio', difficile: 'Difficile' };
const DIFFICULTY_COLORS = { facile: '#10B981', medio: '#F59E0B', difficile: '#EF4444' };
const POLLUTION_LABELS = { poco: 'Poco sporco', medio: 'Mediamente sporco', tanto: 'Molto sporco' };
const POLLUTION_COLORS = { poco: '#10B981', medio: '#F59E0B', tanto: '#EF4444' };

const MapContent = ({ collections, sosReports, wasteReports, underwaterReports, places, naturalSites, onMarkerClick, selectedMarker, onTakeReport, onResolveReport, onCancelTake, currentUserId, language }) => {
  const map = useMap();

  useEffect(() => {
    if (map && window.google) {
      const allPoints = [...collections, ...sosReports, ...(wasteReports || []), ...(underwaterReports || []), ...(places || []), ...(naturalSites || [])];
      if (allPoints.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        allPoints.forEach((point) => {
          if (point.location?.lat && point.location?.lng) {
            bounds.extend({ lat: point.location.lat, lng: point.location.lng });
          }
        });
        map.fitBounds(bounds);
        setTimeout(() => { if (allPoints.length === 1) map.setZoom(12); }, 100);
      }
    }
  }, [map, collections, sosReports, wasteReports, underwaterReports, places, naturalSites]);

  return (
    <>
      {collections.map((collection, index) => (
        collection.location?.lat && collection.location?.lng && (
          <AdvancedMarker
            key={`collection-${index}`}
            position={{ lat: collection.location.lat, lng: collection.location.lng }}
            onClick={() => onMarkerClick({ type: 'collection', data: collection, index })}
          >
            <div className="cursor-pointer transform hover:scale-125 transition-transform">
              {collection.is_sea_collection ? (
                <div className="w-7 h-7 rounded-full bg-orange-500 border-2 border-white shadow-lg flex items-center justify-center">
                  <Ship className="w-4 h-4 text-white" />
                </div>
              ) : (
                <img
                  src="https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/likcbe5b_IMG_6596.jpeg"
                  alt="Raccolta"
                  className="w-7 h-7 rounded-full border-2 border-white shadow-lg object-cover"
                />
              )}
            </div>
          </AdvancedMarker>
        )
      ))}

      {/* Segnalazioni Rifiuti (triangolo giallo/arancione/verde) */}
      {wasteReports && wasteReports.map((report, index) => (
        report.location?.lat && report.location?.lng && (
          <AdvancedMarker
            key={`waste-${index}`}
            position={{ lat: report.location.lat, lng: report.location.lng }}
            onClick={() => onMarkerClick({ type: 'waste', data: report, index })}
          >
            <div className="cursor-pointer transform hover:scale-125 transition-transform">
              {report.status === 'resolved' ? (
                // Cerchio verde con check per resolved
                <div className="w-6 h-6 rounded-full bg-emerald-500 border-2 border-white shadow-lg flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
              ) : (
                // Triangolo giallo/arancione per pending/taken
                <div className={`w-0 h-0 border-l-[12px] border-r-[12px] border-b-[22px] border-l-transparent border-r-transparent ${
                  report.status === 'taken' ? 'border-b-orange-500' : 'border-b-yellow-400'
                } filter drop-shadow-lg flex items-center justify-center relative`}>
                  <span className="absolute top-[6px] text-black font-bold text-sm">!</span>
                </div>
              )}
            </div>
          </AdvancedMarker>
        )
      ))}

      {sosReports.map((sos, index) => (
        sos.location?.lat && sos.location?.lng && (
          <AdvancedMarker
            key={`sos-${index}`}
            position={{ lat: sos.location.lat, lng: sos.location.lng }}
            onClick={() => onMarkerClick({ type: 'sos', data: sos, index })}
          >
            <div className="cursor-pointer transform hover:scale-125 transition-transform">
              <div className="w-7 h-7 rounded-full bg-red-600 border-2 border-white shadow-lg flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-white" />
              </div>
            </div>
          </AdvancedMarker>
        )
      ))}

      {/* Segnalazioni Subacquee (icona sub blu) */}
      {underwaterReports && underwaterReports.map((report, index) => (
        report.location?.lat && report.location?.lng && (
          <AdvancedMarker
            key={`underwater-${index}`}
            position={{ lat: report.location.lat, lng: report.location.lng }}
            onClick={() => onMarkerClick({ type: 'underwater', data: report, index })}
          >
            <div className="cursor-pointer transform hover:scale-125 transition-transform">
              <div className={`w-7 h-7 rounded-full border-2 border-white shadow-lg flex items-center justify-center ${
                report.status === 'taken' ? 'bg-orange-500' : 'bg-blue-500'
              }`}>
                <span className="text-sm">🤿</span>
              </div>
            </div>
          </AdvancedMarker>
        )
      ))}

      {/* Luoghi da Scoprire (stella verde/turchese) */}
      {places && places.map((place, index) => (
        place.location?.lat && place.location?.lng && (
          <AdvancedMarker
            key={`place-${index}`}
            position={{ lat: place.location.lat, lng: place.location.lng }}
            onClick={() => onMarkerClick({ type: 'place', data: place, index })}
          >
            <div className="cursor-pointer transform hover:scale-125 transition-transform">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 border-2 border-white shadow-lg flex items-center justify-center">
                <Star className="w-4 h-4 text-white fill-white" />
              </div>
            </div>
          </AdvancedMarker>
        )
      ))}

      {/* Siti Naturalistici del Mediterraneo */}
      {naturalSites && naturalSites.map((site, index) => (
        site.location?.lat && site.location?.lng && (
          <AdvancedMarker
            key={`natural-${index}`}
            position={{ lat: site.location.lat, lng: site.location.lng }}
            onClick={() => onMarkerClick({ type: 'natural_site', data: site, index })}
          >
            <div className="cursor-pointer transform hover:scale-125 transition-transform">
              {site.site_type === 'amp' ? (
                // Area Marina Protetta - Onde blu (più piccolo)
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 border-2 border-white shadow-lg flex items-center justify-center">
                  <Waves className="w-4 h-4 text-white" />
                </div>
              ) : site.site_type === 'centro_recupero' ? (
                // Centro Recupero Tartarughe - Icona tartaruga marina circolare
                <div className="w-9 h-9 rounded-full overflow-hidden shadow-lg border-2 border-white">
                  <img src="/turtle-circle.png" alt="Centro Recupero" className="w-full h-full object-cover" />
                </div>
              ) : (
                // Oasi Faunistica - Cannocchiale arancione
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-500 to-orange-700 border-2 border-white shadow-lg flex items-center justify-center">
                  <Search className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
          </AdvancedMarker>
        )
      ))}

      {selectedMarker && (
        <InfoWindow
          key={`${selectedMarker.data.id || selectedMarker.data._id}-${language}`}
          position={{ lat: selectedMarker.data.location.lat, lng: selectedMarker.data.location.lng }}
          onCloseClick={() => onMarkerClick(null)}
          options={{ maxWidth: 350, minWidth: 280 }}
        >
          <div style={{ minWidth: '260px', maxWidth: '320px', padding: '4px', position: 'relative' }}>
            {/* Pulsante X di chiusura - dentro il riquadro bianco */}
            <button
              onClick={() => onMarkerClick(null)}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                backgroundColor: '#6b7280',
                border: 'none',
                color: '#fff',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
            >
              ✕
            </button>
            {selectedMarker.type === 'collection' ? (
              <CollectionPopup data={selectedMarker.data} />
            ) : selectedMarker.type === 'waste' ? (
              <WastePopup 
                data={selectedMarker.data} 
                onTake={onTakeReport}
                onResolve={onResolveReport}
                onCancel={onCancelTake}
                currentUserId={currentUserId}
              />
            ) : selectedMarker.type === 'underwater' ? (
              <UnderwaterPopup data={selectedMarker.data} />
            ) : selectedMarker.type === 'place' ? (
              <PlacePopup data={selectedMarker.data} />
            ) : selectedMarker.type === 'natural_site' ? (
              <NaturalSitePopup data={selectedMarker.data} />
            ) : (
              <SOSPopup data={selectedMarker.data} />
            )}
          </div>
        </InfoWindow>
      )}
    </>
  );
};

// Popup completo per raccolta
const CollectionPopup = ({ data }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const activeMaterials = data.categories 
    ? Object.entries(data.categories).filter(([_, val]) => val > 0)
    : [];

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif' }}>
      {/* FOTO SPIAGGIA */}
      {data.photo_url && (
        <div style={{ margin: '-4px -4px 12px -4px' }}>
          <img 
            src={data.photo_url} 
            alt="Foto spiaggia" 
            style={{ 
              width: '100%', 
              height: '140px', 
              objectFit: 'cover', 
              borderRadius: '8px 8px 0 0',
              display: 'block'
            }}
          />
        </div>
      )}

      {/* NOME VOLONTARIO */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px', 
        marginBottom: '8px',
        paddingBottom: '8px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <User style={{ width: '16px', height: '16px', color: '#0891b2' }} />
        <span style={{ fontWeight: '600', color: '#1f2937', fontSize: '14px' }}>
          Raccolta di: {data.volunteer_name || 'Volontario'}
        </span>
      </div>

      {/* KG E CREDITS */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <Droplet style={{ width: '16px', height: '16px', color: '#06b6d4' }} />
        <span style={{ fontWeight: '700', color: '#0891b2', fontSize: '16px' }}>{data.kg_collected} kg</span>
        <span style={{ color: '#9ca3af' }}>•</span>
        <span style={{ fontWeight: '600', color: '#7c3aed' }}>{data.kg_collected * 10} Credits</span>
      </div>

      {/* DATA */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', color: '#6b7280', fontSize: '12px' }}>
        <Calendar style={{ width: '12px', height: '12px' }} />
        <span>{formatDate(data.timestamp)}</span>
      </div>

      {/* MATERIALI */}
      {activeMaterials.length > 0 && (
        <div style={{ marginBottom: '10px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
          <p style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px', fontWeight: '500' }}>Materiali raccolti:</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {activeMaterials.map(([key, val]) => (
              <span 
                key={key} 
                style={{ 
                  fontSize: '11px', 
                  backgroundColor: '#e0f2fe', 
                  color: '#0369a1', 
                  padding: '2px 8px', 
                  borderRadius: '4px' 
                }}
              >
                {CATEGORY_LABELS[key] || key}: {val}kg
              </span>
            ))}
          </div>
        </div>
      )}

      {/* NOME SPIAGGIA */}
      {data.place && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '6px' }}>
          <MapPin style={{ width: '14px', height: '14px', color: '#10b981', marginTop: '2px', flexShrink: 0 }} />
          <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>{data.place}</span>
        </div>
      )}

      {/* DIFFICOLTÀ */}
      {data.difficulty && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
          <Mountain style={{ width: '14px', height: '14px', color: '#6b7280' }} />
          <span style={{ fontSize: '12px', color: '#374151' }}>Difficoltà: </span>
          <span style={{ 
            fontSize: '11px', 
            padding: '2px 8px', 
            borderRadius: '4px',
            backgroundColor: DIFFICULTY_COLORS[data.difficulty] || '#6b7280',
            color: 'white',
            fontWeight: '500'
          }}>
            {DIFFICULTY_LABELS[data.difficulty] || data.difficulty}
          </span>
        </div>
      )}

      {/* SEGNALETICA */}
      {data.has_signage !== null && data.has_signage !== undefined && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
          <SignpostBig style={{ width: '14px', height: '14px', color: '#6b7280' }} />
          <span style={{ fontSize: '12px', color: '#374151' }}>Segnaletica: </span>
          <span style={{ 
            fontSize: '11px', 
            padding: '2px 8px', 
            borderRadius: '4px',
            backgroundColor: data.has_signage ? '#10b981' : '#ef4444',
            color: 'white',
            fontWeight: '500'
          }}>
            {data.has_signage ? 'Presente' : 'Assente'}
          </span>
        </div>
      )}

      {/* LIVELLO SPORCIZIA */}
      {data.pollution_level && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
          <Trash style={{ width: '14px', height: '14px', color: '#6b7280' }} />
          <span style={{ fontSize: '12px', color: '#374151' }}>Sporcizia: </span>
          <span style={{ 
            fontSize: '11px', 
            padding: '2px 8px', 
            borderRadius: '4px',
            backgroundColor: POLLUTION_COLORS[data.pollution_level] || '#6b7280',
            color: 'white',
            fontWeight: '500'
          }}>
            {POLLUTION_LABELS[data.pollution_level] || data.pollution_level}
          </span>
        </div>
      )}

      {/* AVVISTAMENTI */}
      {data.animal_sightings && (
        <div style={{ 
          marginTop: '8px', 
          paddingTop: '8px', 
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f0fdf4',
          padding: '8px',
          borderRadius: '6px',
          marginLeft: '-4px',
          marginRight: '-4px'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
            <Bird style={{ width: '14px', height: '14px', color: '#16a34a', marginTop: '2px', flexShrink: 0 }} />
            <div>
              <span style={{ fontSize: '11px', color: '#15803d', fontWeight: '600', display: 'block', marginBottom: '2px' }}>
                Avvistamenti:
              </span>
              <span style={{ fontSize: '12px', color: '#166534' }}>{data.animal_sightings}</span>
            </div>
          </div>
        </div>
      )}

      {/* NO FOTO MESSAGE */}
      {!data.photo_url && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '4px', 
          color: '#9ca3af', 
          fontSize: '11px',
          marginTop: '8px'
        }}>
          <ImageIcon style={{ width: '12px', height: '12px' }} />
          <span>Nessuna foto</span>
        </div>
      )}
    </div>
  );
};

// Mappa categorie SOS con immagini
const SOS_CATEGORY_IMAGES = {
  'Tartarughe': 'https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/vro0bdj7_IMG_6704.jpeg',
  'Foche': 'https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/h3e1bl6v_IMG_6705.jpeg',
  'Cetacei': 'https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/siqdwpih_IMG_6697.jpeg',
  'Uccelli Marini': 'https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/d721edbb_IMG_6702.jpeg',
  'Pesci': 'https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/eud3kxo1_IMG_6703.jpeg',
  'Oggetti Grandi': 'https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/9s5f4r60_IMG_6701.jpeg',
  'Reti e Cime': 'https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/pf77o4nv_IMG_6700.jpeg'
};

const ANIMAL_STATUS_LABELS = { vivo: 'Vivo', morto: 'Morto', non_so: 'Non determinato' };
const ANIMAL_STATUS_COLORS = { vivo: '#10B981', morto: '#EF4444', non_so: '#6B7280' };
const ANIMAL_LOCATION_LABELS = { acqua: 'In acqua', spiaggia: 'In spiaggia' };
const ANIMAL_LOCATION_COLORS = { acqua: '#0EA5E9', spiaggia: '#F59E0B' };
const WOUNDS_LABELS = { si: 'Sì', no: 'No', non_visibili: 'Non visibili' };

// Popup per SOS con foto, icona categoria e dettagli
const SOSPopup = ({ data }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const categoryImage = SOS_CATEGORY_IMAGES[data.category];
  const hasDetails = data.animal_status || data.animal_location || data.has_wounds || data.has_nets !== null;

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif' }}>
      {/* FOTO EMERGENZA */}
      {data.photo_url && (
        <div style={{ margin: '-4px -4px 12px -4px' }}>
          <img 
            src={data.photo_url} 
            alt="Foto emergenza" 
            style={{ 
              width: '100%', 
              height: '140px', 
              objectFit: 'cover', 
              borderRadius: '8px 8px 0 0',
              display: 'block'
            }}
          />
        </div>
      )}

      {/* Icona categoria e titolo */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: '12px', 
        marginBottom: '12px',
        paddingBottom: '12px',
        borderBottom: '2px solid #fecaca'
      }}>
        {categoryImage && (
          <img 
            src={categoryImage} 
            alt={data.category}
            style={{ 
              width: '60px', 
              height: '60px', 
              objectFit: 'contain'
            }}
          />
        )}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AlertTriangle style={{ width: '16px', height: '16px', color: '#dc2626' }} />
            <span style={{ fontWeight: '700', color: '#dc2626', fontSize: '14px' }}>
              SOS
            </span>
          </div>
          <span style={{ fontWeight: '600', color: '#374151', fontSize: '16px', display: 'block' }}>
            {data.category}
          </span>
        </div>
      </div>

      {/* Dettagli animale */}
      {hasDetails && (
        <div style={{ 
          marginBottom: '12px', 
          padding: '10px', 
          backgroundColor: '#1e293b', 
          borderRadius: '8px',
          marginLeft: '-4px',
          marginRight: '-4px'
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {data.animal_status && (
              <span style={{ 
                fontSize: '11px', 
                padding: '3px 8px', 
                borderRadius: '4px',
                backgroundColor: ANIMAL_STATUS_COLORS[data.animal_status] || '#6b7280',
                color: 'white',
                fontWeight: '500'
              }}>
                {ANIMAL_STATUS_LABELS[data.animal_status]}
              </span>
            )}
            {data.animal_location && (
              <span style={{ 
                fontSize: '11px', 
                padding: '3px 8px', 
                borderRadius: '4px',
                backgroundColor: ANIMAL_LOCATION_COLORS[data.animal_location] || '#6b7280',
                color: 'white',
                fontWeight: '500'
              }}>
                {ANIMAL_LOCATION_LABELS[data.animal_location]}
              </span>
            )}
            {data.has_wounds && (
              <span style={{ 
                fontSize: '11px', 
                padding: '3px 8px', 
                borderRadius: '4px',
                backgroundColor: data.has_wounds === 'si' ? '#ef4444' : data.has_wounds === 'no' ? '#10b981' : '#6b7280',
                color: 'white',
                fontWeight: '500'
              }}>
                Ferite: {WOUNDS_LABELS[data.has_wounds]}
              </span>
            )}
            {data.has_nets !== null && data.has_nets !== undefined && (
              <span style={{ 
                fontSize: '11px', 
                padding: '3px 8px', 
                borderRadius: '4px',
                backgroundColor: data.has_nets ? '#ef4444' : '#10b981',
                color: 'white',
                fontWeight: '500'
              }}>
                Reti: {data.has_nets ? 'Sì' : 'No'}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Segnalatore */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
        <User style={{ width: '14px', height: '14px', color: '#6b7280' }} />
        <span style={{ fontSize: '13px', color: '#374151' }}>
          Segnalato da: {data.volunteer_name || 'Volontario'}
        </span>
      </div>

      {/* Data */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', color: '#6b7280', fontSize: '12px' }}>
        <Calendar style={{ width: '12px', height: '12px' }} />
        <span>{formatDate(data.timestamp)}</span>
      </div>

      {/* Coordinate GPS */}
      {data.location && (
        <div style={{ 
          marginTop: '8px',
          padding: '8px',
          backgroundColor: '#f0f9ff',
          borderRadius: '6px',
          marginLeft: '-4px',
          marginRight: '-4px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#0891b2', fontSize: '11px' }}>
            <MapPin style={{ width: '12px', height: '12px' }} />
            <span style={{ fontFamily: 'monospace' }}>
              {data.location.lat?.toFixed(5)}, {data.location.lng?.toFixed(5)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// Popup per Segnalazione Rifiuti
const WastePopup = ({ data, onTake, onResolve, onCancel, currentUserId }) => {
  const [loading, setLoading] = useState(false);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
    });
  };

  const handleAction = async (action) => {
    setLoading(true);
    try {
      if (action === 'take') await onTake(data.id);
      else if (action === 'resolve') await onResolve(data.id);
      else if (action === 'cancel') await onCancel(data.id);
    } finally {
      setLoading(false);
    }
  };

  const isOwner = data.taken_by_id === currentUserId;
  const isTaken = data.status === 'taken';
  const isResolved = data.status === 'resolved';

  // Colori in base allo stato
  const statusColor = isResolved ? '#10b981' : isTaken ? '#f97316' : '#eab308';
  const statusBg = isResolved ? '#d1fae5' : isTaken ? '#ffedd5' : '#fef3c7';
  const statusText = isResolved ? 'Pulizia completata!' : isTaken ? `In carico a: ${data.taken_by_name}` : 'Da rimuovere';

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif' }}>
      {/* FOTO RIFIUTO */}
      {data.photo_url && (
        <div style={{ margin: '-4px -4px 12px -4px' }}>
          <img 
            src={data.photo_url} 
            alt="Foto rifiuto" 
            style={{ 
              width: '100%', 
              height: '140px', 
              objectFit: 'cover', 
              borderRadius: '8px 8px 0 0',
              display: 'block'
            }}
          />
        </div>
      )}

      {/* Header con stato */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: '8px', 
        marginBottom: '10px',
        paddingBottom: '8px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{
          width: isResolved ? '20px' : '0',
          height: isResolved ? '20px' : '0',
          borderLeft: isResolved ? 'none' : '10px solid transparent',
          borderRight: isResolved ? 'none' : '10px solid transparent',
          borderBottom: isResolved ? 'none' : `18px solid ${statusColor}`,
          backgroundColor: isResolved ? statusColor : 'transparent',
          borderRadius: isResolved ? '50%' : '0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {isResolved && <CheckCircle style={{ width: '14px', height: '14px', color: 'white' }} />}
        </div>
        <div>
          <span style={{ fontWeight: '700', color: '#1f2937', fontSize: '14px', display: 'block' }}>
            {isResolved ? 'Rifiuto rimosso' : 'Rifiuto da rimuovere'}
          </span>
          <span style={{ 
            fontSize: '11px', 
            padding: '2px 6px', 
            borderRadius: '4px',
            backgroundColor: statusBg,
            color: isResolved ? '#065f46' : isTaken ? '#c2410c' : '#92400e',
            fontWeight: '500'
          }}>
            {statusText}
          </span>
        </div>
      </div>

      {/* Descrizione */}
      <div style={{ 
        padding: '10px',
        backgroundColor: isResolved ? '#d1fae5' : '#fef3c7',
        borderRadius: '8px',
        marginBottom: '10px'
      }}>
        <p style={{ fontSize: '13px', color: isResolved ? '#065f46' : '#92400e', margin: 0 }}>
          <TranslatedText text={data.description} />
        </p>
      </div>

      {/* Segnalatore */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
        <User style={{ width: '14px', height: '14px', color: '#6b7280' }} />
        <span style={{ fontSize: '13px', color: '#374151' }}>
          Segnalato da: {data.reporter_name || 'Anonimo'}
        </span>
      </div>

      {/* Data */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280', fontSize: '12px', marginBottom: '12px' }}>
        <Calendar style={{ width: '12px', height: '12px' }} />
        <span>{formatDate(data.created_at)}</span>
      </div>

      {/* PULSANTI AZIONE */}
      {!isResolved && (
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
          {!isTaken ? (
            // Nessuno ha preso in carico - mostra "Prendi in carico"
            <button
              onClick={() => handleAction('take')}
              disabled={loading}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '14px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <HandMetal style={{ width: '16px', height: '16px' }} />
              {loading ? 'Caricamento...' : 'Prendi in carico'}
            </button>
          ) : isOwner ? (
            // È stato preso dall'utente corrente - mostra opzioni
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleAction('resolve')}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '13px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <CheckCircle style={{ width: '14px', height: '14px' }} />
                Pulizia fatta
              </button>
              <button
                onClick={() => handleAction('cancel')}
                disabled={loading}
                style={{
                  padding: '10px 12px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontWeight: '500',
                  fontSize: '13px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
              >
                Annulla
              </button>
            </div>
          ) : (
            // È stato preso da qualcun altro - messaggio informativo
            <div style={{
              padding: '10px',
              backgroundColor: '#fef3c7',
              borderRadius: '8px',
              textAlign: 'center',
              fontSize: '12px',
              color: '#92400e'
            }}>
              Questo rifiuto è già in carico a un altro volontario
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Popup per segnalazione subacquea
const UnderwaterPopup = ({ data }) => {
  const ITEM_TYPES = {
    'reti_fantasma': { label: 'Reti fantasma', icon: '🕸️' },
    'pneumatici': { label: 'Pneumatici', icon: '🛞' },
    'ancore': { label: 'Ancore/Catene', icon: '⚓' },
    'plastica': { label: 'Rifiuti plastici', icon: '🥤' },
    'metallo': { label: 'Rottami metallici', icon: '🔩' },
    'altro': { label: 'Altro', icon: '❓' }
  };

  const typeInfo = ITEM_TYPES[data.item_type] || { label: data.item_type, icon: '❓' };

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #3b82f6, #0ea5e9)', 
        margin: '-4px -4px 12px -4px', 
        padding: '16px', 
        borderRadius: '8px 8px 0 0',
        color: 'white'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '24px' }}>🤿</span>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>Segnalazione Subacquea</h3>
            <p style={{ margin: '4px 0 0', fontSize: '12px', opacity: 0.9 }}>
              {typeInfo.icon} {typeInfo.label}
            </p>
          </div>
        </div>
      </div>

      {/* Contenuto */}
      <div style={{ padding: '0 4px' }}>
        {/* Descrizione */}
        <p style={{ margin: '0 0 12px', color: '#334155', fontSize: '14px', lineHeight: '1.5' }}>
          <TranslatedText text={data.description} />
        </p>

        {/* Info */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
          {data.depth && (
            <span style={{ 
              background: '#e0f2fe', 
              color: '#0369a1', 
              padding: '4px 8px', 
              borderRadius: '6px', 
              fontSize: '12px',
              fontWeight: '500'
            }}>
              📏 {data.depth}
            </span>
          )}
          {data.difficulty && (
            <span style={{ 
              background: data.difficulty === 'facile' ? '#dcfce7' : data.difficulty === 'medio' ? '#fef3c7' : '#fee2e2',
              color: data.difficulty === 'facile' ? '#166534' : data.difficulty === 'medio' ? '#92400e' : '#991b1b',
              padding: '4px 8px', 
              borderRadius: '6px', 
              fontSize: '12px',
              fontWeight: '500'
            }}>
              {data.difficulty === 'facile' ? '🟢' : data.difficulty === 'medio' ? '🟡' : '🔴'} {data.difficulty}
            </span>
          )}
          <span style={{ 
            background: data.status === 'active' ? '#dbeafe' : '#ffedd5',
            color: data.status === 'active' ? '#1e40af' : '#c2410c',
            padding: '4px 8px', 
            borderRadius: '6px', 
            fontSize: '12px',
            fontWeight: '500'
          }}>
            {data.status === 'active' ? '🔵 Da recuperare' : '🟠 In carico'}
          </span>
        </div>

        {/* Notes */}
        {data.notes && (
          <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: '12px', fontStyle: 'italic' }}>
            📝 {data.notes}
          </p>
        )}

        {/* Footer */}
        <div style={{ 
          borderTop: '1px solid #e2e8f0', 
          paddingTop: '12px', 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '12px', color: '#64748b' }}>
            <p style={{ margin: 0 }}>🤿 {data.reporter_name}</p>
          </div>
          {data.taken_by_name && (
            <div style={{ fontSize: '11px', color: '#f97316', textAlign: 'right' }}>
              In carico a: {data.taken_by_name}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Popup per Luoghi da Scoprire
// Popup per Siti Naturalistici del Mediterraneo
const NaturalSitePopup = ({ data }) => {
  const SITE_TYPE_INFO = {
    amp: { label: 'Area Marina Protetta', color: '#3b82f6', icon: '🌊', gradient: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' },
    centro_recupero: { label: 'Centro Recupero Tartarughe', color: '#22c55e', icon: '🐢', gradient: 'linear-gradient(135deg, #22c55e, #15803d)' },
    oasi_faunistica: { label: 'Oasi Faunistica', color: '#f97316', icon: '🔭', gradient: 'linear-gradient(135deg, #f97316, #c2410c)' }
  };

  const siteInfo = SITE_TYPE_INFO[data.site_type] || SITE_TYPE_INFO.amp;

  const openNavigator = () => {
    const { lat, lng } = data.location;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    let url;
    if (isIOS) {
      url = `maps://maps.apple.com/?daddr=${lat},${lng}`;
    } else if (isAndroid) {
      url = `google.navigation:q=${lat},${lng}`;
    } else {
      url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    }
    window.open(url, '_blank');
  };

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif' }}>
      {/* FOTO SITO */}
      {data.photo_url && (
        <div style={{ margin: '-4px -4px 12px -4px' }}>
          <img 
            src={data.photo_url} 
            alt={data.name}
            style={{ 
              width: '100%', 
              height: '140px', 
              objectFit: 'cover', 
              borderRadius: '8px 8px 0 0',
              display: 'block'
            }}
          />
        </div>
      )}

      {/* Header con icona tipo sito */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: '10px', 
        marginBottom: '10px',
        paddingBottom: '10px',
        borderBottom: `2px solid ${siteInfo.color}`
      }}>
        <div style={{
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          background: data.site_type === 'centro_recupero' ? 'transparent' : siteInfo.gradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: '22px',
          overflow: 'hidden',
          border: data.site_type === 'centro_recupero' ? 'none' : 'none'
        }}>
          {data.site_type === 'centro_recupero' ? (
            <img src="/turtle-circle.png" alt="Centro Recupero" style={{ width: '44px', height: '44px', objectFit: 'cover', borderRadius: '50%' }} />
          ) : siteInfo.icon}
        </div>
        <div style={{ flex: 1 }}>
          <span style={{ fontWeight: '700', color: '#1f2937', fontSize: '15px', display: 'block', lineHeight: '1.3' }}>
            {data.name}
          </span>
          <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
            <span style={{ 
              fontSize: '10px', 
              padding: '2px 8px', 
              borderRadius: '4px',
              backgroundColor: siteInfo.color,
              color: 'white',
              fontWeight: '600'
            }}>
              {siteInfo.label}
            </span>
            {data.country && (
              <span style={{ 
                fontSize: '10px', 
                padding: '2px 8px', 
                borderRadius: '4px',
                backgroundColor: '#e2e8f0',
                color: '#475569',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '3px'
              }}>
                <Globe style={{ width: '10px', height: '10px' }} />
                {data.country}{data.region ? ` - ${data.region}` : ''}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Descrizione */}
      <p style={{ 
        margin: '0 0 12px', 
        color: '#374151', 
        fontSize: '13px', 
        lineHeight: '1.5',
        maxHeight: '80px',
        overflow: 'hidden'
      }}>
        <TranslatedText text={data.description} />
      </p>

      {/* Contatti */}
      <div style={{ marginBottom: '12px' }}>
        {data.phone && (
          <a 
            href={`tel:${data.phone}`}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              color: '#059669',
              textDecoration: 'none',
              fontSize: '13px',
              fontWeight: '500',
              marginBottom: '6px'
            }}
          >
            <Phone style={{ width: '14px', height: '14px' }} />
            {data.phone}
          </a>
        )}
        {data.email && (
          <a 
            href={`mailto:${data.email}`}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              color: '#0369a1',
              textDecoration: 'none',
              fontSize: '13px',
              marginBottom: '6px'
            }}
          >
            📧 {data.email}
          </a>
        )}
        {data.website && (
          <a 
            href={data.website}
            target="_blank"
            rel="noopener noreferrer"
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              color: '#7c3aed',
              textDecoration: 'none',
              fontSize: '13px'
            }}
          >
            <ExternalLink style={{ width: '14px', height: '14px' }} />
            Sito web ufficiale
          </a>
        )}
      </div>

      {/* Pulsante Portami qui */}
      <button
        onClick={openNavigator}
        style={{
          width: '100%',
          padding: '10px 16px',
          background: siteInfo.gradient,
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
      >
        <Navigation style={{ width: '16px', height: '16px' }} />
        Portami qui
      </button>
    </div>
  );
};

const PlacePopup = ({ data }) => {
  const PLACE_TYPE_LABELS = {
    spiaggia: 'Spiaggia', sentiero: 'Sentiero', baia: 'Baia',
    scogliera: 'Scogliera', riserva: 'Riserva Naturale', altro: 'Altro'
  };
  const DIFFICULTY_INFO = {
    facile: { label: 'Facile', color: '#10b981' },
    medio: { label: 'Medio', color: '#f59e0b' },
    difficile: { label: 'Difficile', color: '#ef4444' }
  };

  const diffInfo = DIFFICULTY_INFO[data.difficulty] || DIFFICULTY_INFO.facile;

  const openNavigator = () => {
    const { lat, lng } = data.location;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    let url;
    if (isIOS) {
      url = `maps://maps.apple.com/?daddr=${lat},${lng}`;
    } else if (isAndroid) {
      url = `google.navigation:q=${lat},${lng}`;
    } else {
      url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    }
    window.open(url, '_blank');
  };

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif' }}>
      {/* FOTO LUOGO */}
      {data.photo_url && (
        <div style={{ margin: '-4px -4px 12px -4px' }}>
          <img 
            src={data.photo_url} 
            alt={data.name}
            style={{ 
              width: '100%', 
              height: '140px', 
              objectFit: 'cover', 
              borderRadius: '8px 8px 0 0',
              display: 'block'
            }}
          />
        </div>
      )}

      {/* Header con stella */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: '10px', 
        marginBottom: '10px',
        paddingBottom: '10px',
        borderBottom: '2px solid #6ee7b7'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #10b981, #06b6d4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <Star style={{ width: '20px', height: '20px', color: 'white', fill: 'white' }} />
        </div>
        <div>
          <span style={{ fontWeight: '700', color: '#1f2937', fontSize: '16px', display: 'block' }}>
            {data.name}
          </span>
          <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
            <span style={{ 
              fontSize: '10px', 
              padding: '2px 6px', 
              borderRadius: '4px',
              backgroundColor: '#e0f2fe',
              color: '#0369a1',
              fontWeight: '500'
            }}>
              {PLACE_TYPE_LABELS[data.place_type] || 'Luogo'}
            </span>
            <span style={{ 
              fontSize: '10px', 
              padding: '2px 6px', 
              borderRadius: '4px',
              backgroundColor: diffInfo.color,
              color: 'white',
              fontWeight: '500'
            }}>
              {diffInfo.label}
            </span>
          </div>
        </div>
      </div>

      {/* Descrizione */}
      <p style={{ 
        margin: '0 0 10px', 
        color: '#374151', 
        fontSize: '13px', 
        lineHeight: '1.5',
        maxHeight: '60px',
        overflow: 'hidden'
      }}>
        <TranslatedText text={data.description} />
      </p>

      {/* Servizi */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
        {data.has_parking && (
          <span style={{ 
            display: 'flex', alignItems: 'center', gap: '4px',
            fontSize: '11px', backgroundColor: '#e0f2fe', color: '#0369a1',
            padding: '3px 8px', borderRadius: '4px'
          }}>
            <Car style={{ width: '12px', height: '12px' }} /> Parcheggio
          </span>
        )}
        {data.has_bathroom && (
          <span style={{ 
            display: 'flex', alignItems: 'center', gap: '4px',
            fontSize: '11px', backgroundColor: '#e0f2fe', color: '#0369a1',
            padding: '3px 8px', borderRadius: '4px'
          }}>
            🚻 Bagni
          </span>
        )}
        {data.has_food && (
          <span style={{ 
            display: 'flex', alignItems: 'center', gap: '4px',
            fontSize: '11px', backgroundColor: '#e0f2fe', color: '#0369a1',
            padding: '3px 8px', borderRadius: '4px'
          }}>
            <Coffee style={{ width: '12px', height: '12px' }} /> Ristoro
          </span>
        )}
      </div>

      {/* Telefono */}
      {data.phone && (
        <a 
          href={`tel:${data.phone}`}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '8px 12px', backgroundColor: '#f0fdf4',
            borderRadius: '6px', marginBottom: '10px',
            textDecoration: 'none', color: '#166534', fontSize: '13px'
          }}
        >
          <Phone style={{ width: '14px', height: '14px' }} />
          {data.phone}
        </a>
      )}

      {/* Autore */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', color: '#6b7280', fontSize: '12px' }}>
        <User style={{ width: '12px', height: '12px' }} />
        <span>Aggiunto da: {data.created_by || 'Utente'}</span>
      </div>

      {/* Pulsante Portami qui */}
      <button
        onClick={openNavigator}
        style={{
          width: '100%',
          padding: '10px',
          background: 'linear-gradient(135deg, #10b981, #06b6d4)',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontWeight: '600',
          fontSize: '14px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
      >
        <Navigation style={{ width: '16px', height: '16px' }} />
        Portami qui
      </button>
    </div>
  );
};

const MapSection = () => {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const [collections, setCollections] = useState([]);
  const [sosReports, setSosReports] = useState([]);
  const [wasteReports, setWasteReports] = useState([]);
  const [underwaterReports, setUnderwaterReports] = useState([]);
  const [places, setPlaces] = useState([]);
  const [naturalSites, setNaturalSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [center] = useState({ lat: 41.9028, lng: 12.4964 });
  
  // Guida Emme
  const { showGuide, closeGuide, openGuide } = useEmmeGuide('mappa');
  
  // Filtri per i marker
  const [filters, setFilters] = useState({
    collections: true,
    waste: true,
    underwater: true,
    places: true,
    sos: true,
    amp: false,          // Aree Marine Protette - disattivato di default
    centri: false,       // Centri Recupero - disattivato di default
    oasi: false          // Oasi Faunistiche - disattivato di default
  });

  const toggleFilter = (key) => {
    setFilters(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [collectionsRes, sosRes, wasteRes, underwaterRes, placesRes, naturalSitesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/collections/map`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/sos/map`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/waste-reports`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/underwater-reports`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/places`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/natural-sites`).catch((err) => { console.error('Error fetching natural sites:', err); return { data: [] }; })
      ]);
      setCollections(collectionsRes.data);
      setSosReports(sosRes.data);
      setWasteReports(wasteRes.data);
      setUnderwaterReports(underwaterRes.data);
      // Filtra solo i luoghi con posizione GPS
      setPlaces(placesRes.data.filter(p => p.has_location && p.location?.lat && p.location?.lng));
      setNaturalSites(naturalSitesRes.data || []);
      console.log('Natural sites loaded:', naturalSitesRes.data?.length || 0);
    } catch (error) {
      console.error('Error fetching map data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleMarkerClick = useCallback((marker) => setSelectedMarker(marker), []);

  // Funzioni per gestire le segnalazioni
  const handleTakeReport = async (reportId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/waste-reports/${reportId}/take`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Segnalazione presa in carico!');
      fetchData(); // Ricarica i dati
      setSelectedMarker(null);
    } catch (error) {
      console.error('Error taking report:', error);
      toast.error('Errore nella presa in carico');
    }
  };

  const handleResolveReport = async (reportId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/waste-reports/${reportId}/resolve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pulizia completata! Grazie per il tuo contributo!');
      fetchData();
      setSelectedMarker(null);
    } catch (error) {
      console.error('Error resolving report:', error);
      toast.error('Errore nel completamento');
    }
  };

  const handleCancelTake = async (reportId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/waste-reports/${reportId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.info('Presa in carico annullata');
      fetchData();
      setSelectedMarker(null);
    } catch (error) {
      console.error('Error canceling take:', error);
      toast.error('Errore nell\'annullamento');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Guida Emme */}
      {showGuide && <EmmeGuide sectionId="mappa" onClose={closeGuide} />}
      <EmmeHelpButton onClick={openGuide} />
      
      <div className="mb-6">
        <div className="flex justify-center mb-4">
          <img 
            src="https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/likcbe5b_IMG_6596.jpeg" 
            alt="Logo" 
            className="w-24 h-24 rounded-2xl object-cover shadow-lg"
          />
        </div>
        <h2 className="text-3xl font-bold text-white text-center mb-2" style={{fontFamily: 'Barlow Condensed'}}>
          MAPPA COLLABORATIVA
        </h2>
        <p className="text-slate-400 text-center text-sm mb-3">
          Tocca un marker per vedere foto e dettagli
        </p>
        <div className="flex justify-center">
          <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </button>
        </div>
      </div>

      {/* Filtri Marker */}
      <div className="mb-4 flex flex-wrap justify-center gap-2">
        <button
          onClick={() => toggleFilter('collections')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
            filters.collections 
              ? 'bg-cyan-500/20 border border-cyan-500 text-cyan-400' 
              : 'bg-slate-800/50 border border-slate-700 text-slate-500'
          }`}
        >
          <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
          Raccolte
        </button>
        <button
          onClick={() => toggleFilter('waste')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
            filters.waste 
              ? 'bg-yellow-500/20 border border-yellow-500 text-yellow-400' 
              : 'bg-slate-800/50 border border-slate-700 text-slate-500'
          }`}
        >
          <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-b-[8px] border-l-transparent border-r-transparent border-b-yellow-500"></div>
          Segnalazioni
        </button>
        <button
          onClick={() => toggleFilter('underwater')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
            filters.underwater 
              ? 'bg-blue-500/20 border border-blue-500 text-blue-400' 
              : 'bg-slate-800/50 border border-slate-700 text-slate-500'
          }`}
        >
          <span className="text-sm">🤿</span>
          Sub
        </button>
        <button
          onClick={() => toggleFilter('places')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
            filters.places 
              ? 'bg-emerald-500/20 border border-emerald-500 text-emerald-400' 
              : 'bg-slate-800/50 border border-slate-700 text-slate-500'
          }`}
        >
          <Star className="w-3 h-3 fill-current" />
          Luoghi
        </button>
        <button
          onClick={() => toggleFilter('sos')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
            filters.sos 
              ? 'bg-red-500/20 border border-red-500 text-red-400' 
              : 'bg-slate-800/50 border border-slate-700 text-slate-500'
          }`}
        >
          <AlertTriangle className="w-3 h-3" />
          SOS
        </button>
      </div>

      {/* Filtri Siti Naturalistici */}
      <div className="mb-4 flex flex-wrap justify-center gap-2">
        <span className="text-slate-500 text-xs font-medium self-center mr-2">Siti Mediterraneo:</span>
        <button
          onClick={() => toggleFilter('amp')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
            filters.amp 
              ? 'bg-blue-500/20 border border-blue-500 text-blue-400' 
              : 'bg-slate-800/50 border border-slate-700 text-slate-500'
          }`}
        >
          <Waves className="w-3 h-3" />
          AMP ({naturalSites.filter(s => s.site_type === 'amp').length})
        </button>
        <button
          onClick={() => toggleFilter('centri')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
            filters.centri 
              ? 'bg-green-500/20 border border-green-500 text-green-400' 
              : 'bg-slate-800/50 border border-slate-700 text-slate-500'
          }`}
        >
          <span className="text-sm">🐢</span>
          Centri Recupero ({naturalSites.filter(s => s.site_type === 'centro_recupero').length})
        </button>
        <button
          onClick={() => toggleFilter('oasi')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
            filters.oasi 
              ? 'bg-orange-500/20 border border-orange-500 text-orange-400' 
              : 'bg-slate-800/50 border border-slate-700 text-slate-500'
          }`}
        >
          <Search className="w-3 h-3" />
          Oasi ({naturalSites.filter(s => s.site_type === 'oasi_faunistica').length})
        </button>
      </div>
      
      {/* Suggerimento per i filtri */}
      {(filters.amp || filters.centri || filters.oasi) && (
        <p className="text-center text-slate-500 text-xs mb-3">
          💡 Fai zoom sulla mappa per vedere meglio i singoli siti
        </p>
      )}

      <div className="pb-6">
        {loading ? (
          <div className="h-[450px] rounded-2xl border border-white/10 bg-slate-900/60 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="h-[450px] rounded-2xl overflow-hidden border border-white/10">
            <LeafletMap
              collections={filters.collections ? collections : []}
              sosReports={filters.sos ? sosReports : []}
              wasteReports={filters.waste ? wasteReports : []}
              underwaterReports={filters.underwater ? underwaterReports : []}
              places={filters.places ? places : []}
              naturalSites={naturalSites.filter(s => 
                (filters.amp && s.site_type === 'amp') ||
                (filters.centri && s.site_type === 'centro_recupero') ||
                (filters.oasi && s.site_type === 'oasi_faunistica')
              )}
              onMarkerClick={handleMarkerClick}
              center={[center.lat, center.lng]}
              zoom={6}
            />
          </div>
        )}

        <div className="mt-4 grid grid-cols-5 gap-2">
          <div className="bg-slate-900/60 border border-cyan-500/30 rounded-xl p-2 sm:p-3">
            <p className="text-white text-[10px] sm:text-xs text-center mb-1">Raccolte</p>
            <p className="text-cyan-400 text-lg sm:text-2xl font-bold text-center">{collections.length}</p>
          </div>
          <div className="bg-slate-900/60 border border-yellow-500/30 rounded-xl p-2 sm:p-3">
            <p className="text-white text-[10px] sm:text-xs text-center mb-1">Segnalaz.</p>
            <p className="text-yellow-400 text-lg sm:text-2xl font-bold text-center">{wasteReports.length}</p>
          </div>
          <div className="bg-slate-900/60 border border-blue-500/30 rounded-xl p-2 sm:p-3">
            <p className="text-white text-[10px] sm:text-xs text-center mb-1">Sub</p>
            <p className="text-blue-400 text-lg sm:text-2xl font-bold text-center">{underwaterReports.length}</p>
          </div>
          <div className="bg-slate-900/60 border border-emerald-500/30 rounded-xl p-2 sm:p-3">
            <p className="text-white text-[10px] sm:text-xs text-center mb-1">Luoghi</p>
            <p className="text-emerald-400 text-lg sm:text-2xl font-bold text-center">{places.length}</p>
          </div>
          <div className="bg-slate-900/60 border border-red-500/30 rounded-xl p-2 sm:p-3">
            <p className="text-white text-[10px] sm:text-xs text-center mb-1">SOS</p>
            <p className="text-red-400 text-lg sm:text-2xl font-bold text-center">{sosReports.length}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapSection;
